// @ts-nocheck
/**
 * chat-date-picker.js — Custom calendar date picker for the order form.
 *
 * Renders a monthly calendar grid that disables past dates and dates
 * beyond the booking window. Replaces the native <input type="date">
 * which doesn't enforce min/max visually on iOS.
 *
 * Exposes:
 *   window.AIFlorist.datePicker.attach(inputEl, opts)
 *     opts: { minDate, maxDate, blackoutDates, onSelect(isoStr) }
 */
(function () {
  'use strict';

  window.AIFlorist = window.AIFlorist || {};

  var DAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
  var MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];

  /**
   * Attach a calendar picker to a text input.
   * The input becomes read-only; clicking it toggles the calendar.
   * Returns { getValue, setValue, destroy }.
   */
  function attach(inputEl, opts) {
    var minDate = opts.minDate || '';
    var maxDate = opts.maxDate || '';
    var onSelect = opts.onSelect || function () {};
    var blackoutSet = buildBlackoutSet(opts.blackoutDates);
    var selected = '';
    var viewYear, viewMonth;

    inputEl.readOnly = true;
    inputEl.type = 'text';
    inputEl.setAttribute('inputmode', 'none');
    inputEl.style.cursor = 'pointer';

    var wrapper = document.createElement('div');
    wrapper.className = 'afc-cal-wrap';

    var cal = document.createElement('div');
    cal.className = 'afc-cal';
    cal.style.display = 'none';
    wrapper.appendChild(cal);

    inputEl.parentNode.insertBefore(wrapper, inputEl.nextSibling);

    var initialDate = parseISO(minDate) || getSGTNow();
    viewYear = initialDate.getFullYear();
    viewMonth = initialDate.getMonth();

    inputEl.addEventListener('click', function (e) {
      e.preventDefault();
      toggle();
    });

    function toggle() {
      var isOpen = cal.style.display !== 'none';
      if (isOpen) {
        close();
      } else {
        open();
      }
    }

    function open() {
      if (selected) {
        var d = parseISO(selected);
        if (d) { viewYear = d.getFullYear(); viewMonth = d.getMonth(); }
      }
      render();
      cal.style.display = '';
      requestAnimationFrame(function () { cal.classList.add('afc-cal--open'); });
      setTimeout(function () { document.addEventListener('click', outsideClick); }, 0);
    }

    function close() {
      cal.classList.remove('afc-cal--open');
      setTimeout(function () { cal.style.display = 'none'; }, 180);
      document.removeEventListener('click', outsideClick);
    }

    function outsideClick(e) {
      if (!wrapper.contains(e.target) && e.target !== inputEl) close();
    }

    function render() {
      cal.innerHTML = buildHeader() + buildGrid();
      bindCalEvents();
    }

    function buildHeader() {
      var canPrev = canNavigate(-1);
      var canNext = canNavigate(1);
      return '<div class="afc-cal-header">' +
        '<button type="button" class="afc-cal-nav' + (canPrev ? '' : ' afc-cal-nav--disabled') +
          '" data-dir="-1" aria-label="Previous month">' +
          '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
            'stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>' +
        '</button>' +
        '<span class="afc-cal-title">' + MONTHS[viewMonth] + ' ' + viewYear + '</span>' +
        '<button type="button" class="afc-cal-nav' + (canNext ? '' : ' afc-cal-nav--disabled') +
          '" data-dir="1" aria-label="Next month">' +
          '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
            'stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>' +
        '</button>' +
      '</div>';
    }

    function buildGrid() {
      var html = '<div class="afc-cal-grid">';
      for (var i = 0; i < 7; i++) {
        html += '<div class="afc-cal-dow">' + DAYS[i] + '</div>';
      }

      var first = new Date(viewYear, viewMonth, 1);
      var startDay = first.getDay();
      var daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();

      for (var blank = 0; blank < startDay; blank++) {
        html += '<div class="afc-cal-cell afc-cal-cell--empty"></div>';
      }

      for (var d = 1; d <= daysInMonth; d++) {
        var iso = isoDate(new Date(viewYear, viewMonth, d));
        var isOutOfRange = (minDate && iso < minDate) || (maxDate && iso > maxDate);
        var isBlackout = blackoutSet[iso] === true;
        var isDisabled = isOutOfRange || isBlackout;
        var isSelected = iso === selected;
        var isToday = iso === isoDate(getSGTNow());

        var cls = 'afc-cal-cell';
        if (isDisabled) cls += ' afc-cal-cell--disabled';
        if (isBlackout && !isOutOfRange) cls += ' afc-cal-cell--blackout';
        if (isSelected) cls += ' afc-cal-cell--selected';
        if (isToday && !isSelected) cls += ' afc-cal-cell--today';

        html += '<div class="' + cls + '"' +
          (isDisabled ? '' : ' data-date="' + iso + '" role="button" tabindex="0"') +
          '>' + d + '</div>';
      }

      html += '</div>';
      return html;
    }

    function canNavigate(dir) {
      var testMonth = viewMonth + dir;
      var testYear = viewYear;
      if (testMonth < 0) { testMonth = 11; testYear--; }
      if (testMonth > 11) { testMonth = 0; testYear++; }

      if (dir === -1) {
        var lastOfPrev = isoDate(new Date(testYear, testMonth + 1, 0));
        return !minDate || lastOfPrev >= minDate;
      }
      var firstOfNext = isoDate(new Date(testYear, testMonth, 1));
      return !maxDate || firstOfNext <= maxDate;
    }

    function bindCalEvents() {
      var navBtns = cal.querySelectorAll('.afc-cal-nav:not(.afc-cal-nav--disabled)');
      for (var i = 0; i < navBtns.length; i++) {
        navBtns[i].addEventListener('click', function (e) {
          e.stopPropagation();
          var dir = parseInt(this.getAttribute('data-dir'), 10);
          viewMonth += dir;
          if (viewMonth < 0) { viewMonth = 11; viewYear--; }
          if (viewMonth > 11) { viewMonth = 0; viewYear++; }
          render();
        });
      }

      var cells = cal.querySelectorAll('.afc-cal-cell[data-date]');
      for (var j = 0; j < cells.length; j++) {
        cells[j].addEventListener('click', function (e) {
          e.stopPropagation();
          selectDate(this.getAttribute('data-date'));
        });
        cells[j].addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            selectDate(this.getAttribute('data-date'));
          }
        });
      }
    }

    function selectDate(iso) {
      selected = iso;
      var d = parseISO(iso);
      inputEl.value = formatDisplay(d);
      inputEl.setAttribute('data-value', iso);
      close();
      render();
      onSelect(iso);
    }

    function formatDisplay(d) {
      var day = d.getDate();
      var mon = MONTHS[d.getMonth()].slice(0, 3);
      var year = d.getFullYear();
      var dow = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d.getDay()];
      return dow + ', ' + day + ' ' + mon + ' ' + year;
    }

    function setValue(iso) {
      if (!iso) return;
      selected = iso;
      var d = parseISO(iso);
      if (d) {
        inputEl.value = formatDisplay(d);
        inputEl.setAttribute('data-value', iso);
        viewYear = d.getFullYear();
        viewMonth = d.getMonth();
      }
    }

    function getValue() {
      return inputEl.getAttribute('data-value') || '';
    }

    function destroy() {
      document.removeEventListener('click', outsideClick);
      if (wrapper.parentNode) wrapper.parentNode.removeChild(wrapper);
    }

    return { getValue: getValue, setValue: setValue, destroy: destroy };
  }

  // ── Helpers ─────────────────────────────────────────────────────

  function isoDate(d) {
    var y = d.getFullYear();
    var m = String(d.getMonth() + 1).padStart(2, '0');
    var day = String(d.getDate()).padStart(2, '0');
    return y + '-' + m + '-' + day;
  }

  function parseISO(str) {
    if (!str) return null;
    var parts = str.split('-');
    if (parts.length !== 3) return null;
    return new Date(+parts[0], +parts[1] - 1, +parts[2]);
  }

  function getSGTNow() {
    return new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Singapore' }));
  }

  function buildBlackoutSet(dates) {
    var set = {};
    if (!dates || !dates.length) return set;
    for (var i = 0; i < dates.length; i++) {
      set[dates[i]] = true;
    }
    return set;
  }

  window.AIFlorist.datePicker = { attach: attach };
})();
